import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/shared/api/http';

/*
 * Web push API (`/api/notifications/push`, docs/modules/notifications.md "Web push") and its TanStack Query hooks.
 * Query keys: ['notifications', 'push', …].
 */

export interface PushConfig {
  /** The portal has a VAPID key pair (`Push:VapidPublicKey` …); without it nothing below works. */
  enabled: boolean;
  /** The key a browser subscribes with (`applicationServerKey`), base64url. */
  publicKey: string | null;
}

export type PushService = 'google' | 'mozilla' | 'apple' | 'microsoft' | 'emulator' | 'other';

/** A device of the signed-in user that receives push notifications (its address is never returned). */
export interface PushDevice {
  id: string;
  label: string | null;
  service: PushService;
  createdAt: string;
  updatedAt: string;
  lastDeliveredAt: string | null;
}

export interface PushSubscriptionRequest {
  endpoint: string;
  expirationTime: number | null;
  keys: { p256dh: string; auth: string };
  label: string | null;
}

export interface PushTestResult {
  sent: number;
  failed: number;
  removed: number;
}

export const pushKeys = {
  config: ['notifications', 'push', 'config'] as const,
  devices: ['notifications', 'push', 'devices'] as const,
  /** What this browser says (permission, subscription): not server data, but refreshed like it. */
  browser: ['notifications', 'push', 'browser'] as const,
};

export const fetchPushConfig = (signal?: AbortSignal) =>
  api.get<PushConfig>('/notifications/push/config', { signal });

export const subscribePush = (request: PushSubscriptionRequest) =>
  api.put<PushDevice>('/notifications/push/subscription', request);

export const unsubscribePush = (endpoint: string) =>
  api.post('/notifications/push/unsubscribe', { endpoint });

export function usePushConfig() {
  return useQuery({
    queryKey: pushKeys.config,
    queryFn: ({ signal }) => fetchPushConfig(signal),
    // The portal's keys change with a deploy at most.
    staleTime: 10 * 60_000,
  });
}

export function usePushDevices(enabled = true) {
  return useQuery({
    queryKey: pushKeys.devices,
    queryFn: ({ signal }) => api.get<PushDevice[]>('/notifications/push/devices', { signal }),
    enabled,
  });
}

export function useRemovePushDevice() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/notifications/push/devices/${encodeURIComponent(id)}`),
    onSettled: () => client.invalidateQueries({ queryKey: pushKeys.devices }),
  });
}

/** Sends a test push to this device (by its endpoint) or, without one, to every device of the user. */
export function useSendTestPush() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (endpoint: string | null) =>
      api.post<PushTestResult>('/notifications/push/test', endpoint ? { endpoint } : {}),
    onSettled: () => client.invalidateQueries({ queryKey: pushKeys.devices }),
  });
}
